import type { ReactNode } from 'react';
import { CalendarRange, ChevronDown, ListFilter, Search, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateRangeFilter } from '@/components/ui/date-range-filter';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toIsoDay } from '@/lib/datetime';
import { cn } from '@/lib/utils';

/**
 * Shared list-toolbar pieces: a search box, an inline toolbar select, labelled
 * filter rows, a "Filters (n)" popover, a tri-state yes/no switch, a date-range
 * button and a removable-chip row. A list keeps its 2–4 most-used filters inline and folds the rest into the
 * popover (FE-ADR-064).
 */

/** Search box for a list toolbar. */
export function ListSearch({
  value,
  onChange,
  placeholder,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
}) {
  return (
    <div className={cn('relative w-56', className)}>
      <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 pl-8"
        aria-label={placeholder}
      />
    </div>
  );
}

/** Inline toolbar select with an "all" sentinel and no visible label (the
 *  toolbar is a row of these) — `ariaLabel` names it for assistive tech. */
export function ToolbarSelect({
  value,
  onChange,
  allLabel,
  options,
  ariaLabel,
  triggerClassName = 'h-9',
}: {
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: readonly { value: string; label: string }[];
  ariaLabel: string;
  triggerClassName?: string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={triggerClassName} aria-label={ariaLabel}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** A single labelled select with an "all" sentinel option (a popover row). */
export function FilterSelect({
  label,
  value,
  onChange,
  allLabel,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  allLabel: string;
  options: readonly { value: string; label: string }[];
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <ToolbarSelect
        value={value}
        onChange={onChange}
        allLabel={allLabel}
        options={options}
        ariaLabel={label}
        triggerClassName="h-8 w-full"
      />
    </div>
  );
}

/** Any / Yes / No for a boolean filter — `value` is 'all' | 'yes' | 'no'. */
export function TriStateFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const choices = [
    { value: 'all', label: 'Any' },
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
  ];
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium">{label}</span>
      <div role="radiogroup" aria-label={label} className="bg-muted flex rounded-md p-0.5">
        {choices.map((c) => (
          <button
            key={c.value}
            type="button"
            role="radio"
            aria-checked={value === c.value}
            onClick={() => onChange(c.value)}
            className={cn(
              'flex-1 rounded px-2 py-1 text-xs transition-colors',
              value === c.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Filters trigger + popover shell — caller supplies the rows. */
export function FiltersPopover({
  activeFilters,
  onClear,
  children,
  contentClassName,
}: {
  activeFilters: number;
  onClear: () => void;
  children: ReactNode;
  contentClassName?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 gap-1.5">
          <ListFilter className="size-4" /> Filters
          {activeFilters > 0 ? (
            <Badge className="ml-0.5 px-1.5 text-[10px]">{activeFilters}</Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('w-72 space-y-3', contentClassName)}>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Filters</span>
          {activeFilters > 0 ? (
            <button
              type="button"
              onClick={onClear}
              className="text-muted-foreground hover:text-foreground text-xs"
            >
              Clear all
            </button>
          ) : null}
        </div>
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Toolbar button that shows a filter's current value and opens its editor. */
export function FilterButton({
  label,
  value,
  active,
  icon,
  children,
  contentClassName,
}: {
  label: string;
  /** What the filter is set to, shown after the label ("Owner: Me"). */
  value: string;
  active: boolean;
  icon?: ReactNode;
  children: ReactNode;
  contentClassName?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-9 max-w-64 gap-1.5', active && 'border-primary/60')}
          aria-label={`${label}: ${value}`}
        >
          {icon}
          <span className="text-muted-foreground">{label}:</span>
          <span className="truncate">{value}</span>
          <ChevronDown className="text-muted-foreground size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className={cn('w-72 space-y-3', contentClassName)}>
        {children}
      </PopoverContent>
    </Popover>
  );
}

/** Rolling `[from, to]` covering the last `days` days, today included. */
export function lastDaysRange(days: number, today = new Date()): { from: string; to: string } {
  // Calendar-day arithmetic, so a daylight-saving change can't shift the start.
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - (days - 1));
  return { from: toIsoDay(start), to: toIsoDay(today) };
}

/** Rolling windows a date filter offers, in days. */
export const ROLLING_WINDOWS = [7, 30, 90] as const;

/** "1 Oct" for an ISO day; an unparsable (e.g. stale persisted) value shows "—". */
const shortDay = (day: string) => {
  const d = new Date(`${day}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
};

/** Human summary of a date window: "Any time", "Last 7 days", "1 Oct – 12 Oct". */
export function describeDateRange(from: string, to: string, rollingDays = 0): string {
  if (rollingDays > 0) return `Last ${rollingDays} days`;
  if (!from && !to) return 'Any time';
  if (from && to) return `${shortDay(from)} – ${shortDay(to)}`;
  return from ? `From ${shortDay(from)}` : `Until ${shortDay(to)}`;
}

/** A date window as a filter holds it: fixed ends, or a rolling day count that is
 *  re-anchored on today every time it is read (so "Last 7 days" keeps rolling). */
export interface DateWindow {
  from: string;
  to: string;
  rollingDays: number;
}

/** The fixed `[from, to]` a window means right now. */
export function resolveDateWindow(w: DateWindow, today = new Date()): { from: string; to: string } {
  return w.rollingDays > 0 ? lastDaysRange(w.rollingDays, today) : { from: w.from, to: w.to };
}

/** One toolbar button for a date window: rolling presets + the month/quarter picker. */
export function DateRangeButton({
  label,
  idPrefix,
  window: w,
  onChange,
}: {
  label: string;
  idPrefix: string;
  window: DateWindow;
  onChange: (next: DateWindow) => void;
}) {
  const shown = resolveDateWindow(w);
  const anyTime = w.rollingDays === 0 && !w.from && !w.to;
  const fixed = (from: string, to: string) => onChange({ from, to, rollingDays: 0 });
  return (
    <FilterButton
      label={label}
      value={describeDateRange(w.from, w.to, w.rollingDays)}
      active={!anyTime}
      icon={<CalendarRange className="size-4" />}
      contentClassName="w-auto"
    >
      <div className="flex flex-wrap gap-1.5">
        <Button size="xs" variant={anyTime ? 'default' : 'outline'} onClick={() => fixed('', '')}>
          Any time
        </Button>
        {ROLLING_WINDOWS.map((days) => (
          <Button
            key={days}
            size="xs"
            variant={w.rollingDays === days ? 'default' : 'outline'}
            onClick={() => onChange({ from: '', to: '', rollingDays: days })}
          >
            Last {days} days
          </Button>
        ))}
      </div>
      <DateRangeFilter
        label={label}
        idPrefix={idPrefix}
        from={shown.from}
        to={shown.to}
        onFromChange={(v) => fixed(v, shown.to)}
        onToChange={(v) => fixed(shown.from, v)}
        onRangeChange={fixed}
      />
    </FilterButton>
  );
}

export interface ActiveFilterChip {
  key: string;
  /** e.g. "Channel: Upwork". */
  label: string;
  onRemove: () => void;
}

/** Row of removable chips for the filters in force, ending in "Reset all
 *  filters". The caller decides when to show it (it renders even with no chips,
 *  so a list whose only active filters sit in the toolbar still gets one reset). */
export function ActiveFilterChips({
  chips,
  onResetAll,
}: {
  chips: ActiveFilterChip[];
  onResetAll: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-label="Active filters">
      {chips.map((c) => (
        <Badge key={c.key} variant="secondary" className="gap-1 pr-1 font-normal">
          {c.label}
          <button
            type="button"
            onClick={c.onRemove}
            aria-label={`Remove filter ${c.label}`}
            className="hover:bg-muted-foreground/20 rounded-sm p-0.5"
          >
            <X className="size-3" />
          </button>
        </Badge>
      ))}
      <button
        type="button"
        onClick={onResetAll}
        className={cn('text-muted-foreground hover:text-foreground text-xs', chips.length > 0 && 'ml-1')}
      >
        Reset all filters
      </button>
    </div>
  );
}
